import { useEffect, useMemo, useState } from "react";
import { styles } from "../styles";
import BackButton from "../components/BackButton";

const STATUS_BADGE = {
  pending: { label: "Pending", color: "#9a6b00", bg: "#fff3cd" },
  approved: { label: "Approved", color: "#1a7f37", bg: "#c9efc9" },
  locked: { label: "Locked", color: "#3d4a5c", bg: "#dde7f4" },
};

// Seed list used when org hasn't customized pay types yet. Admin can extend
// from Payroll page (Configure Pay Types) and the additions persist on the
// org doc.
export const DEFAULT_PAY_TYPES = [
  { id: "straight", name: "Straight Time", multiplier: 1.0 },
  { id: "overtime", name: "Over Time", multiplier: 1.5 },
  { id: "double", name: "Double Time", multiplier: 2.0 },
];

const formatDuration = (mins) => {
  if (!mins || mins <= 0) return "0:00";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
};

const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

const nowLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

// Cascading customer -> location -> job picker. Forces a real job
// selection — flat dropdowns become unusable past a few hundred jobs.
function JobPicker({ customers = [], jobsRaw = [], value, onChange }) {
  // Derive the current selection's parents from the chosen jobId so the
  // dropdowns stay in sync even after page reloads.
  const selectedJob = jobsRaw.find((j) => j.id === value.jobId);
  const customerId = value.customerId || selectedJob?.customerId || "";
  const locationId = value.locationId || selectedJob?.locationId || "";

  const customer = customers.find((c) => c.id === customerId);
  const locations = customer?.locations || [];

  // Jobs scoped to the picked customer+location. Hide closed/cancelled
  // jobs unless they're already the selected one (so reopening an entry
  // mid-edit doesn't drop the job out of the list).
  const candidateJobs = jobsRaw.filter(
    (j) =>
      j.customerId === customerId &&
      j.locationId === locationId &&
      (j.status !== "completed" && j.status !== "cancelled"
        ? true
        : j.id === value.jobId)
  );

  const sortedCustomers = customers
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const sortedLocations = locations
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const sortedJobs = candidateJobs.slice().sort((a, b) => {
    const an = Number(a.jobNumber) || 0;
    const bn = Number(b.jobNumber) || 0;
    return bn - an;
  });

  return (
    <div
      style={{ display: "flex", gap: "8px", flexWrap: "wrap", maxWidth: "780px" }}
    >
      <div>
        <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
          Customer
        </label>
        <select
          style={{ ...styles.input, margin: 0, width: "220px" }}
          value={customerId}
          onChange={(e) =>
            onChange({ customerId: e.target.value, locationId: "", jobId: "" })
          }
        >
          <option value="">(pick customer)</option>
          {sortedCustomers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || `Customer ${c.id.slice(0, 6)}`}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
          Location
        </label>
        <select
          style={{ ...styles.input, margin: 0, width: "220px" }}
          value={locationId}
          onChange={(e) =>
            onChange({ customerId, locationId: e.target.value, jobId: "" })
          }
          disabled={!customerId}
        >
          <option value="">
            {customerId ? "(pick location)" : "(pick customer first)"}
          </option>
          {sortedLocations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name || `Location ${l.id.slice(0, 6)}`}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
          Job
        </label>
        <select
          style={{ ...styles.input, margin: 0, width: "260px" }}
          value={value.jobId || ""}
          onChange={(e) =>
            onChange({ customerId, locationId, jobId: e.target.value })
          }
          disabled={!locationId}
        >
          <option value="">
            {locationId ? "(pick job)" : "(pick location first)"}
          </option>
          {sortedJobs.map((j) => (
            <option key={j.id} value={j.id}>
              {j.jobNumber ? `#${j.jobNumber} — ` : ""}
              {j.title || j.id.slice(0, 8)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default function MyTimePage({
  user,
  actions,
  customers = [],
  timeEntriesRaw = [],
  jobsRaw = [],
  payTypes,
  onBack,
}) {
  const effectivePayTypes = (payTypes && payTypes.length > 0)
    ? payTypes
    : DEFAULT_PAY_TYPES;

  const myEntries = useMemo(
    () => timeEntriesRaw.filter((e) => e.userId === user?.uid),
    [timeEntriesRaw, user]
  );

  const openClock = useMemo(
    () => myEntries.find((e) => e.source === "clock" && !e.endAt),
    [myEntries]
  );

  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    if (!openClock) return;
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [openClock]);

  const elapsedFormatted = openClock?.startAt
    ? (() => {
        const ms = tick - new Date(openClock.startAt).getTime();
        const totalSec = Math.max(0, Math.floor(ms / 1000));
        const h = Math.floor(totalSec / 3600);
        const m = Math.floor((totalSec % 3600) / 60);
        const s = totalSec % 60;
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(h)}:${pad(m)}:${pad(s)}`;
      })()
    : "00:00:00";

  const [range, setRange] = useState("week");
  const filteredEntries = useMemo(() => {
    if (range === "all") return myEntries;
    const cutoff = Date.now() - (range === "week" ? 7 : 30) * 86400000;
    return myEntries.filter((e) => {
      const t = new Date(e.endAt || e.startAt || e.createdAt).getTime();
      return t >= cutoff;
    });
  }, [myEntries, range]);

  const totalMinutes = filteredEntries.reduce(
    (sum, e) => sum + (e.durationMinutes || 0),
    0
  );

  // --- Clock-in form ---
  const [clockPick, setClockPick] = useState({
    customerId: "",
    locationId: "",
    jobId: "",
  });
  const [clockNote, setClockNote] = useState("");
  const [showClock, setShowClock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleStart = async () => {
    if (!clockPick.jobId) {
      setErr("Pick a customer, location, and job before starting the clock.");
      return;
    }
    try {
      setBusy(true);
      setErr("");
      await actions.startTimeClock(clockPick.jobId, clockNote);
      setClockNote("");
      setClockPick({ customerId: "", locationId: "", jobId: "" });
      setShowClock(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    try {
      setBusy(true);
      setErr("");
      await actions.stopTimeClock();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // --- Manual entry form ---
  const [showManual, setShowManual] = useState(false);
  const [manualPick, setManualPick] = useState({
    customerId: "",
    locationId: "",
    jobId: "",
  });
  const [manualStart, setManualStart] = useState(nowLocal());
  const [manualEnd, setManualEnd] = useState(nowLocal());
  const [manualNotes, setManualNotes] = useState("");
  const [manualPayType, setManualPayType] = useState("straight");

  const resetManual = () => {
    setManualPick({ customerId: "", locationId: "", jobId: "" });
    setManualStart(nowLocal());
    setManualEnd(nowLocal());
    setManualNotes("");
    setManualPayType("straight");
    setShowManual(false);
  };

  const handleManualAdd = async () => {
    if (!manualPick.jobId) {
      setErr("Pick a customer, location, and job for this entry.");
      return;
    }
    if (!manualStart || !manualEnd) {
      setErr("Start and end times are required.");
      return;
    }
    if (new Date(manualEnd) <= new Date(manualStart)) {
      setErr("End time must be after start time.");
      return;
    }
    try {
      setBusy(true);
      setErr("");
      await actions.addManualTimeEntry({
        jobId: manualPick.jobId,
        startAt: new Date(manualStart).toISOString(),
        endAt: new Date(manualEnd).toISOString(),
        notes: manualNotes,
        payType: manualPayType,
      });
      resetManual();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const jobLabel = (jobId) => {
    if (!jobId) return "(no job)";
    const j = jobsRaw.find((x) => x.id === jobId);
    return j
      ? `${j.jobNumber ? `#${j.jobNumber} — ` : ""}${j.title || "untitled"}`
      : `Job ${jobId.slice(0, 6)}`;
  };

  const payTypeLabel = (id) => {
    const pt = effectivePayTypes.find((p) => p.id === id);
    return pt ? pt.name : id || "Straight Time";
  };

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>My Time</h2>

      {err && (
        <div style={{ color: "red", marginBottom: "8px", fontSize: "13px" }}>
          {err}
        </div>
      )}

      {openClock ? (
        <div
          style={{
            ...styles.card,
            maxWidth: "780px",
            background: "#e8f4ee",
            borderColor: "#1a7f37",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <strong style={{ color: "#1a7f37" }}>● Clocked in</strong>
            <span
              style={{
                fontFamily: "monospace",
                fontSize: "22px",
                fontWeight: 700,
                color: "#1a7f37",
              }}
            >
              {elapsedFormatted}
            </span>
          </div>
          <div style={{ marginTop: "6px", fontSize: "13px" }}>
            {jobLabel(openClock.jobId)}
            {openClock.notes && (
              <div style={{ fontStyle: "italic", color: "#444" }}>
                "{openClock.notes}"
              </div>
            )}
            <div style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
              Started {formatDateTime(openClock.startAt)}
            </div>
          </div>
          <button
            style={{ ...styles.primaryButton, marginTop: "10px" }}
            onClick={handleStop}
            disabled={busy}
          >
            {busy ? "Stopping..." : "Stop Clock"}
          </button>
        </div>
      ) : showClock ? (
        <div style={{ ...styles.card, maxWidth: "780px" }}>
          <strong>Start a Clock</strong>
          <div style={{ marginTop: "8px" }}>
            <JobPicker
              customers={customers}
              jobsRaw={jobsRaw}
              value={clockPick}
              onChange={setClockPick}
            />
          </div>
          <div style={{ marginTop: "8px" }}>
            <label style={{ display: "block", marginBottom: "4px" }}>
              Note (optional):
            </label>
            <input
              style={{ ...styles.input, maxWidth: "400px" }}
              value={clockNote}
              onChange={(e) => setClockNote(e.target.value)}
              placeholder="What are you working on?"
            />
          </div>
          <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
            <button
              style={{ ...styles.primaryButton, margin: 0 }}
              onClick={handleStart}
              disabled={busy}
            >
              {busy ? "Starting..." : "Start Clock"}
            </button>
            <button
              style={{ ...styles.button, margin: 0 }}
              onClick={() => {
                setShowClock(false);
                setClockNote("");
                setClockPick({ customerId: "", locationId: "", jobId: "" });
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : !showManual ? (
        <div
          style={{
            display: "flex",
            gap: "10px",
            maxWidth: "780px",
            marginBottom: "12px",
            flexWrap: "wrap",
          }}
        >
          <button
            style={{ ...styles.primaryButton, margin: 0, flex: 1, minWidth: "180px" }}
            onClick={() => setShowClock(true)}
            disabled={busy}
          >
            Start Clock
          </button>
          <button
            style={{ ...styles.primaryButton, margin: 0, flex: 1, minWidth: "180px" }}
            onClick={() => setShowManual(true)}
            disabled={busy}
          >
            + Manual Entry
          </button>
        </div>
      ) : null}

      {showManual && (
        <div style={{ ...styles.card, maxWidth: "780px" }}>
          <strong>Manual Time Entry</strong>
            <div style={{ marginTop: "8px" }}>
              <JobPicker
                customers={customers}
                jobsRaw={jobsRaw}
                value={manualPick}
                onChange={setManualPick}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginTop: "8px",
                flexWrap: "wrap",
              }}
            >
              <div>
                <label style={{ display: "block", marginBottom: "4px" }}>
                  Start:
                </label>
                <input
                  style={{ ...styles.input, maxWidth: "220px" }}
                  type="datetime-local"
                  value={manualStart}
                  onChange={(e) => setManualStart(e.target.value)}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "4px" }}>
                  End:
                </label>
                <input
                  style={{ ...styles.input, maxWidth: "220px" }}
                  type="datetime-local"
                  value={manualEnd}
                  onChange={(e) => setManualEnd(e.target.value)}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "4px" }}>
                  Pay Type:
                </label>
                <select
                  style={{ ...styles.input, maxWidth: "220px" }}
                  value={manualPayType}
                  onChange={(e) => setManualPayType(e.target.value)}
                >
                  {effectivePayTypes.map((pt) => (
                    <option key={pt.id} value={pt.id}>
                      {pt.name} ({pt.multiplier}x)
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ marginTop: "8px" }}>
              <label style={{ display: "block", marginBottom: "4px" }}>
                Notes (optional):
              </label>
              <input
                style={{ ...styles.input, maxWidth: "400px" }}
                value={manualNotes}
                onChange={(e) => setManualNotes(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
              <button
                style={{ ...styles.primaryButton, margin: 0 }}
                onClick={handleManualAdd}
                disabled={busy}
              >
                {busy ? "Saving..." : "Save Entry"}
              </button>
              <button
                style={{ ...styles.button, margin: 0 }}
                onClick={resetManual}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginTop: "16px",
          marginBottom: "8px",
        }}
      >
        <h3 style={{ margin: 0 }}>My Time History</h3>
        <select
          style={{ ...styles.input, maxWidth: "150px", margin: 0 }}
          value={range}
          onChange={(e) => setRange(e.target.value)}
        >
          <option value="week">Last 7 days</option>
          <option value="month">Last 30 days</option>
          <option value="all">All time</option>
        </select>
        <span style={{ fontSize: "13px", color: "#666" }}>
          Total: <strong>{formatDuration(totalMinutes)}</strong>
        </span>
      </div>

      {filteredEntries.length === 0 ? (
        <p style={{ color: "#666", fontStyle: "italic" }}>No entries.</p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {filteredEntries
            .slice()
            .sort(
              (a, b) =>
                new Date(b.startAt || b.createdAt) -
                new Date(a.startAt || a.createdAt)
            )
            .map((e) => {
              const badge = STATUS_BADGE[e.status || "pending"];
              const isPending = (e.status || "pending") === "pending";
              return (
                <li key={e.id} style={{ ...styles.card, maxWidth: "780px" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <strong>{formatDate(e.startAt)}</strong>
                    <span style={{ fontSize: "13px" }}>
                      {formatDuration(e.durationMinutes)}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        padding: "2px 8px",
                        background: badge.bg,
                        color: badge.color,
                        borderRadius: "10px",
                        fontWeight: 600,
                      }}
                    >
                      {badge.label}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        padding: "2px 8px",
                        background: "#eef",
                        color: "#33c",
                        borderRadius: "10px",
                      }}
                    >
                      {payTypeLabel(e.payType)}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginLeft: "auto",
                      }}
                    >
                      {e.source === "clock" ? "Clock" : "Manual"}
                    </span>
                  </div>
                  <div style={{ fontSize: "13px", marginTop: "4px" }}>
                    {jobLabel(e.jobId)}
                  </div>
                  {e.notes && (
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#444",
                        fontStyle: "italic",
                        marginTop: "4px",
                      }}
                    >
                      "{e.notes}"
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    {formatDateTime(e.startAt)} → {formatDateTime(e.endAt)}
                  </div>
                  {isPending && (
                    <div style={{ marginTop: "8px" }}>
                      <button
                        style={{
                          ...styles.button,
                          margin: 0,
                          background: "#fee",
                          color: "#a00",
                        }}
                        onClick={() => {
                          if (window.confirm("Delete this time entry?")) {
                            actions.deleteTimeEntry(e.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
        </ul>
      )}
    </>
  );
}
